import { getMomentTz } from "@dexteel/mesf-core";
import {
	CheckBoxOutlineBlankRounded,
	CheckBoxRounded,
	ChevronLeftRounded,
	ChevronRightRounded,
} from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	ButtonBase,
	LinearProgress,
	Paper,
	Skeleton,
	Snackbar,
	Typography,
} from "@mui/material";
import { useState } from "react";
import { CardPaperSx } from "@/pages/sawlines/components/primitives";
import type { SawTabletTabProps } from "../sawTabletConfig";
import { TabletQualityEditor } from "./components/TabletQualityEditor";
import { QualityStatusChip } from "./components/QualityStatusChip";
import { useSawBundlesByShift } from "@/pages/sawlines/hooks/useSawBundlesByShift";
import type { SawShiftBundle } from "@/pages/sawlines/repositories/sawBundlesRepository";
import {
	NO_CHECK,
	qualityStatus,
} from "@/pages/sawlines/utils/qualityCheckMeasurements";
import { cutLabel } from "./models/qualityCheck";

const moment = getMomentTz();

export const BundleQualityCheckTab = ({ assetId }: SawTabletTabProps) => {
	// null = current shift
	const [shiftId, setShiftId] = useState<number | null>(null);
	const [pendingOnly, setPendingOnly] = useState(false);
	const [selection, setSelection] = useState<SawShiftBundle | null>(null);
	const [busy, setBusy] = useState(false);
	const [notice, setNotice] = useState("");
	const { data, isLoading, isFetching, error, refetch } = useSawBundlesByShift(
		assetId,
		shiftId,
	);
	// live: the shift may roll over while browsing past ones
	const currentShiftId = useSawBundlesByShift(assetId).data?.shift.ShiftId;
	const selected =
		data?.rows.find((cut) => cut.BundleId === selection?.BundleId) ?? selection;
	const rows =
		data?.rows.filter((cut) => !pendingOnly || qualityStatus(cut).pending) ??
		[];
	const shift = data?.shift;

	const changeShift = (next: number | null) => {
		setShiftId(next);
		setSelection(null);
	};

	// Stale `data` is fine: only the just-saved cut changed, and it's skipped.
	const selectNextUnchecked = () => {
		if (!selection) return;
		// rows newest first → findLast = first unchecked cut after the saved one
		const next = data?.rows.findLast(
			(cut) =>
				cut.ShiftSeq > selection.ShiftSeq &&
				qualityStatus(cut).label === NO_CHECK,
		);
		if (next) setSelection(next);
	};

	return (
		<Box
			sx={{
				height: "100%",
				width: "100%",
				flex: 1,
				minWidth: 0,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				gap: 1,
			}}
		>
			{error && (
				<Alert
					severity="error"
					action={<Button onClick={() => void refetch()}>Retry</Button>}
				>
					{error.message}
				</Alert>
			)}
			<Box sx={{ display: "flex", flex: 1, minHeight: 0, gap: 1.5 }}>
				<Paper
					sx={{
						...CardPaperSx,
						p: 0,
						width: { xs: 164, lg: 200 },
						flexShrink: 0,
						minHeight: 0,
						overflow: "hidden",
					}}
				>
					<Box
						sx={{
							// narrow list: shift label on top, arrows below
							display: "flex",
							flexWrap: "wrap",
							gap: 0.75,
							p: 1,
							"& button": { flex: 1, minWidth: 0, height: 44, p: 0 },
						}}
					>
						<Button
							variant="contained"
							aria-label="Previous shift"
							disabled={busy || shift?.PreviousShiftId == null}
							onClick={() => changeShift(shift?.PreviousShiftId ?? null)}
						>
							<ChevronLeftRounded />
						</Button>
						<Typography
							variant="body2"
							fontWeight={500}
							sx={{ order: -1, flexBasis: "100%", textAlign: "center" }}
						>
							{!shift
								? "Loading…"
								: shift.ShiftId == null
									? "Current shift"
									: `${shift.ShiftLabel ?? "Shift"} · ${moment.utc(shift.ProductionDate).format("MMM D")}`}
							<Typography
								component="span"
								variant="caption"
								color="text.secondary"
								sx={{ display: "block" }}
							>
								{shift?.Crew ? `Crew ${shift.Crew}` : "\u00a0"}
							</Typography>
						</Typography>
						<Button
							variant="contained"
							aria-label="Next shift"
							disabled={busy || shiftId == null || shift?.NextShiftId == null}
							onClick={() =>
								changeShift(
									shift?.NextShiftId === currentShiftId
										? null
										: (shift?.NextShiftId ?? null),
								)
							}
						>
							<ChevronRightRounded />
						</Button>
					</Box>
					<Box
						sx={{
							px: 1,
							pb: 1,
							borderBottom: "1px solid",
							borderColor: "divider",
						}}
					>
						<Button
							fullWidth
							variant={pendingOnly ? "contained" : "outlined"}
							startIcon={
								pendingOnly ? (
									<CheckBoxRounded />
								) : (
									<CheckBoxOutlineBlankRounded />
								)
							}
							aria-pressed={pendingOnly}
							onClick={() => setPendingOnly(!pendingOnly)}
							sx={{ minHeight: 44 }}
						>
							Pending{data ? ` · ${data.pendingCount}` : ""}
						</Button>
					</Box>
					{isFetching && !isLoading && (
						<LinearProgress aria-label="Refreshing cuts" />
					)}
					<Box
						sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}
						aria-label="Cuts in selected shift"
					>
						{isLoading
							? [1, 2, 3, 4, 5].map((index) => (
									<Skeleton
										key={index}
										variant="rounded"
										height={80}
										sx={{ m: 1 }}
									/>
								))
							: rows.map((cut) => {
									const status = qualityStatus(cut);
									return (
										<ButtonBase
											key={cut.BundleId}
											disabled={busy}
											onClick={() => setSelection(cut)}
											aria-pressed={selected?.BundleId === cut.BundleId}
											sx={{
												display: "flex",
												flexDirection: "column",
												alignItems: "flex-start",
												gap: 0.25,
												p: 1.25,
												width: "100%",
												textAlign: "left",
												borderBottom: "1px solid",
												borderColor: "divider",
												borderLeft: "6px solid",
												borderLeftColor:
													selected?.BundleId === cut.BundleId
														? "primary.main"
														: "transparent",
												bgcolor:
													selected?.BundleId === cut.BundleId
														? "#e8f5e9"
														: "transparent",
												"&.Mui-focusVisible": {
													outline: "2px solid",
													outlineColor: "primary.main",
													outlineOffset: -2,
												},
											}}
										>
											<Typography
												sx={{
													fontSize: { xs: 17, lg: 20 },
													lineHeight: 1.3,
													fontWeight: 700,
													fontVariantNumeric: "tabular-nums",
												}}
											>
												{moment(cut.StartTime).format("HH:mm")}
											</Typography>
											<Box sx={{ minWidth: 0 }}>
												<Typography variant="body2" fontWeight={700}>
													{cutLabel(cut)} · {cut.BarCount ?? "—"} pcs
												</Typography>
												<Typography
													variant="caption"
													color="text.secondary"
													sx={{ display: "block", overflowWrap: "anywhere" }}
												>
													{cut.PartNumber ??
														(cut.IsCutback ? "Cutback" : "No work order")}
												</Typography>
												<QualityStatusChip {...status} />
											</Box>
										</ButtonBase>
									);
								})}
						{!isLoading && !error && rows.length === 0 && (
							<Typography color="text.secondary" sx={{ p: 2 }}>
								{data?.shift.ShiftId == null
									? "No shift scheduled."
									: pendingOnly
										? "Nothing pending this shift."
										: "No cuts in this shift."}
							</Typography>
						)}
					</Box>
				</Paper>
				{selected ? (
					<TabletQualityEditor
						key={`${assetId}-${selected.BundleId}`}
						cut={selected}
						onBusyChange={setBusy}
						onNotice={setNotice}
						onSaved={selectNextUnchecked}
					/>
				) : (
					<Paper
						sx={{
							...CardPaperSx,
							flex: 1,
							justifyContent: "center",
							alignItems: "center",
							minHeight: 0,
						}}
					>
						{isLoading ? (
							<Skeleton variant="rounded" width="80%" height="65%" />
						) : (
							<Typography color="text.secondary">
								Select a bundle to measure.
							</Typography>
						)}
					</Paper>
				)}
			</Box>
			<Snackbar
				open={notice !== ""}
				autoHideDuration={4500}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				message={notice}
				onClose={() => setNotice("")}
			/>
		</Box>
	);
};
